import React from "react";
import {EventType} from "../../../../../api/generated";
import {useAppDispatch} from "../../../../../app/hooks";
import {getEventTypeTitle} from "../../../../../utils/enumValueTitleUtil";
import {SelectOption} from "../../../../elements/select/SelectDropdown";
import {CustomSwitchFieldWithTitle} from "../../../../elements/customSwitchFieldWithTitle/CustomSwitchFieldWithTitle";
import {ValidatedInputField} from "../../../../elements/validator/component/ValidatedInputField";
import {ValidatedMultiSelectField} from "../../../../elements/validator/component/ValidatedMultiSelectField";
import {ValidatedTextAreaField} from "../../../../elements/validator/component/ValidatedTextAreaField";
import {FormContext} from "../../../../elements/validator/validatedForm/ValidatedForm";
import {NotEmptyListValidator, NotEmptyStringValidator} from "../../../../elements/validator/Validator";
import styles from "./TranscriptRoutingTab.module.css";
import {
    removeRoutingDepartment,
    selectRoutingDepartmentEvents,
    selectRoutingDepartmentFiltrationEnabled,
    selectRoutingDepartmentGuidance,
    selectRoutingDepartmentName,
    TranscriptRoutingSettingsUI,
    updateRoutingDepartment
} from "./transcriptRoutingSlice";

const eventOptions: SelectOption<EventType>[] = Object.values(EventType)
    .map(value => ({label: getEventTypeTitle(value), value}));

interface TranscriptRoutingDepartmentCardProps {
    index: number;
    formContext: FormContext<TranscriptRoutingSettingsUI>;
}

export const TranscriptRoutingDepartmentCard: React.FC<TranscriptRoutingDepartmentCardProps> = ({
    index,
    formContext
}) => {
    const dispatch = useAppDispatch();
    const {updateValidationStatus, getMessage} = formContext;
    const getMessageWrapper = (field: string) => getMessage(field) || "";
    const fieldPrefix = `departments.${index}`;

    return <div className={styles.departmentCard}>
        <div className={styles.departmentHeader}>
            <span className={styles.departmentTitle}>Department {index + 1}</span>
            <button
                type="button"
                className={styles.iconActionButton}
                onClick={() => dispatch(removeRoutingDepartment(index))}
                aria-label={`Remove department ${index + 1}`}
            >
                <img src="/deleteIcon.svg" alt=""/>
            </button>
        </div>
        <div className={styles.departmentFields}>
            <ValidatedInputField<string, TranscriptRoutingSettingsUI>
                validator={NotEmptyStringValidator}
                selector={selectRoutingDepartmentName(index)}
                fieldName={`${fieldPrefix}.name`}
                onChange={name => dispatch(updateRoutingDepartment({index, patch: {name}}))}
                updateValidationStatus={updateValidationStatus}
                label="Department name"
                getMessage={getMessageWrapper}
            />
            <ValidatedTextAreaField<string, TranscriptRoutingSettingsUI>
                validator={NotEmptyStringValidator}
                selector={selectRoutingDepartmentGuidance(index)}
                fieldName={`${fieldPrefix}.summaryGuidance` as keyof TranscriptRoutingSettingsUI}
                onChange={summaryGuidance => dispatch(updateRoutingDepartment({index, patch: {summaryGuidance}}))}
                updateValidationStatus={updateValidationStatus}
                label="Summary guidance"
                maxLength={2000}
                getMessage={getMessageWrapper}
            />
            <CustomSwitchFieldWithTitle
                title="Transcript Filtration"
                description="Only chats with a stored user email or phone number will be sent to this department."
                textOnTrue="On"
                textOnFalse="Off"
                valueSelector={selectRoutingDepartmentFiltrationEnabled(index)}
                toggleValue={filtrationEnabled => dispatch(updateRoutingDepartment({
                    index,
                    patch: {filtrationEnabled}
                }))}
            />
            <ValidatedMultiSelectField<EventType, TranscriptRoutingSettingsUI>
                validator={NotEmptyListValidator}
                selector={selectRoutingDepartmentEvents(index)}
                fieldName={`${fieldPrefix}.eventTypes` as keyof TranscriptRoutingSettingsUI}
                onChange={eventTypes => dispatch(updateRoutingDepartment({index, patch: {eventTypes}}))}
                updateValidationStatus={updateValidationStatus}
                label="Key events"
                options={eventOptions}
                getMessage={getMessageWrapper}
                isEqual={(first, second) => first === second}
            />
        </div>
    </div>;
};
